import { Container } from '@mui/material';
import { APIProvider } from '@vis.gl/react-google-maps';
import { useCallback, useMemo, useState } from 'react';
import { useAuthContext } from 'src/auth/hooks';
import { DisplayPageData } from 'src/components/display-page-data';
import NoneFoundReport from 'src/components/none-found-report';
import PageFilterLoader, { usePageFilterLoaderData } from 'src/components/page-filter-loader';
import PageHeader from 'src/components/page-header';
import PageInfoCarousel from 'src/components/page-info-carousel';
import historyListImage from 'src/assets/images/route-buddy/history-list.png';
import historyDrawerImage from 'src/assets/images/route-buddy/history-drawer.png';
import { useSettingsContext } from 'src/components/settings';
import useModelData from 'src/hooks/use-model-data';
import { EldDriver } from 'src/models';
import { GOOGLE_MAPS_API_KEY } from 'src/config-global';
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { addDaysToDate, today } from 'src/utils/date-utils';
import { Trip } from '../types';
import { tripDurationHours, tripStats } from './history-utils';
import TripHistoryDrawer from './trip-history-drawer';
import TripHistoryTable, { HistoryLine } from './trip-history-table';

type PageModel = {
  trips?: Trip[];
  eldDrivers?: EldDriver[];
};

const pageInfo = (
  <PageInfoCarousel
    steps={[
      {
        image: historyListImage,
        title: 'Pick the dates',
        text: "Choose the first and the last day. Click 'Go'. You see all trips that finished in that time.",
      },
      {
        title: 'Find a trip',
        text: 'Search by driver name or trip name. Click a column name to sort the list.',
      },
      {
        image: historyDrawerImage,
        title: 'Open a trip',
        text: "Click a row. You see the plan next to what really happened, stop by stop. A stop is 'on time' when the truck arrived within 15 minutes of the appointment.",
      },
    ]}
  />
);

function PageContent() {
  const settings = useSettingsContext();
  const { session } = useAuthContext();
  const timezone = (session?.organization?.companyTimezone as string) || 'America/Chicago';

  const pageFilterData = usePageFilterLoaderData(addDaysToDate(today(), -30), today());

  const dataLoader = useCallback(
    () => ({
      trips: !pageFilterData.fromDate
        ? []
        : axios
            .get(formatEndpoint(endpoints.trips.history, pageFilterData))
            .then((response) => response.data),
      eldDrivers: axios.get(endpoints.eldDrivers.findAll).then((response) => response.data),
    }),
    [pageFilterData]
  );
  const { data, error, loading, loaded, setShouldRefresh } = useModelData<PageModel>(dataLoader);

  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);

  const driverNameById = useMemo(() => {
    const map = new Map<number, string>();
    data?.eldDrivers?.forEach((driver) => map.set(driver.id, `${driver.firstName} ${driver.lastName}`));
    return map;
  }, [data?.eldDrivers]);

  const trips = useMemo(() => data?.trips || [], [data?.trips]);

  const lines = useMemo(
    (): HistoryLine[] =>
      trips.map((trip) => {
        const stats = tripStats(trip);
        return {
          tripId: trip.id,
          driverName: driverNameById.get(trip.eldDriverId) || `Driver #${trip.eldDriverId}`,
          tripName: trip.name || `Trip #${trip.id}`,
          status: trip.status,
          departedAt: trip.startedAt || trip.plannedDepartAt,
          endedAt: trip.finishedAt || trip.abandonedAt,
          durationHours: tripDurationHours(trip),
          stopsOnTimeText:
            stats.appointmentStops > 0 ? `${stats.onTimeStops}/${stats.appointmentStops}` : '—',
          onTimeRatio:
            stats.appointmentStops > 0 ? stats.onTimeStops / stats.appointmentStops : null,
          worstLatenessHours: stats.worstLatenessHours,
          raw: trip,
        };
      }),
    [trips, driverNameById]
  );

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Trip History" info={pageInfo} />

        <PageFilterLoader pageFilterData={pageFilterData} onRun={() => setShouldRefresh(true)} />

        {loaded && trips.length === 0 && <NoneFoundReport />}

        {trips.length > 0 && (
          <TripHistoryTable lines={lines} timezone={timezone} onSelectTrip={setSelectedTrip} />
        )}

        <TripHistoryDrawer
          trip={selectedTrip}
          driverName={
            selectedTrip
              ? driverNameById.get(selectedTrip.eldDriverId) || `Driver #${selectedTrip.eldDriverId}`
              : ''
          }
          timezone={timezone}
          onClose={() => setSelectedTrip(null)}
        />
      </DisplayPageData>
    </Container>
  );
}

export default function Page() {
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <PageContent />
    </APIProvider>
  );
}
